import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { StyleSheet, Text } from "react-native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import moment from "moment"
import "moment/locale/fr"
import DatePickerModalM from "@common/components/modal/DatePickerModalM"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout, {
    bandSpacerHeight,
} from "@common/components/funnel/FunnelStepLayout"
import { useFunnelSectionScroll } from "@common/components/funnel/useFunnelSectionScroll"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { SUBJECT_GOAL_DEFAULT } from "@common/components/funnel/funnelSubjectGoals"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import { resolveFunnelUserTimezone } from "@common/student-diagnostic-stepper/constraintsGrid"
import { DimensionsContext } from "@common/utils/mobileUtils"
import SummerSubjectHoursModal from "../summer/components/SummerSubjectHoursModal"
import FormatInfoSheet from "./components/FormatInfoSheet"
import FormulaFooter from "./components/FormulaFooter"
import SlotsPickerModal from "./components/SlotsPickerModal"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { subjectIconUri } from "../summer/constants/summerContent"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { useFormulaChoice } from "./hooks/useFormulaChoice"
import { useInscriptionLead } from "./hooks/useInscriptionLead"
import {
    subjectsOfferedBy,
    useInscriptionSubjects,
} from "./hooks/useInscriptionSubjects"
import {
    answersRefusalMessage,
    inscriptionFunnelService,
} from "./services/inscriptionFunnelService"
import InscriptionComposer from "./components/InscriptionComposer"
import {
    INSCRIPTION_FUNNEL_LOST,
    INSCRIPTION_QUESTIONS,
    INSCRIPTION_STEPS,
} from "./constants/inscriptionContent"
import {
    DEFAULT_OFFER_TYPE,
    FORMAT_STEP,
    INSCRIPTION_FORMATS,
} from "./constants/inscriptionFormats"
import {
    allocationFor,
    hoursLockedTo,
    pickFormat,
    quoteIsWithoutCommitment,
    startOptionCards,
    toggleCommitment,
} from "./utils/formulaChoice"
import {
    formatInfoCopy,
    formatSessions,
    formatTiles,
} from "./utils/formatSessions"
import {
    allowsMoreSubjectsThanHours,
    subjectsFitHours,
} from "./utils/subjectsFitHours"

/**
 * Étape « Composez la formule » — le parent règle et voit, sur UN écran, tout
 * ce qu'il achète : matières, objectif visé sur chacune, volume hebdomadaire
 * (et sa répartition), disponibilités, date de démarrage, format de cours,
 * engagement, et le prix qui en découle.
 *
 * L'écran vend la même formule que `subscribe/create-subscription` (mêmes
 * offres, même grille de disponibilités, même répartition d'heures), posée
 * comme le compositeur du Week-end Réussite : questions numérotées, tout se
 * coche sur l'écran, révélation par paliers, refus qui se voit. Les formats
 * y portent leurs noms (Solo, Tandem) et s'expliquent par le volet du « i ».
 *
 * Aucun montant n'est calculé ici : chaque modification est écrite au serveur,
 * qui renvoie le devis. Ce qui est affiché est ce qui sera débité — y compris
 * l'engagement : l'interrupteur DEMANDE, et le pied de page ne dit que ce que
 * le devis vend. Les tuiles et l'interrupteur, eux, suivent le geste sans
 * attendre : c'est le serveur qui annonce ce qui se vend sans engagement.
 */
/** Une rafale de clics (crans, créneaux) ne part qu'une fois retombée. */
const PERSIST_DEBOUNCE_MS = 400

const InscriptionConfigurationScreen = (props) => {
    const {
        navigation,
        startDateFloor,
        startDateCeiling,
        weeklyHoursByOffer,
        maxSubjectsFromServer,
        recommendedWeeklyHours,
        noCommitment,
        priceGrid,
    } = props
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useInscriptionFunnel()
    const { nextParams } = useInscriptionFunnelGuard()
    const { funnelStateId, formatStep } = useInscriptionLead()
    // Le format a sa propre page pour ce dossier : cette étape ne le demande
    // pas et ne chiffre rien, c'est la suivante qui le fait.
    const formatOnOwnStep = formatStep === FORMAT_STEP.ownStep
    const subjectsCatalogue = useInscriptionSubjects(state.studentClass)

    const [subjectIds, setSubjectIds] = useState(
        (state.subjects || []).map(String)
    )
    const [subjectGoals, setSubjectGoals] = useState(state.subjectGoals || {})
    const [offerType, setOfferType] = useState(
        state.offerType || DEFAULT_OFFER_TYPE
    )
    const [weeklyHours, setWeeklyHours] = useState(state.weeklyHours || 0)
    const [hoursAllocation, setHoursAllocation] = useState(
        state.hoursAllocation || {}
    )
    const [slots, setSlots] = useState(state.slots || [])
    const [slotsOpen, setSlotsOpen] = useState(false)
    const [startDate, setStartDate] = useState(state.startDate || null)
    const [quote, setQuote] = useState(state.quote || null)
    const [dateOpen, setDateOpen] = useState(false)
    // Le format dont le volet « Une heure de {prénom} » est ouvert.
    const [infoFormat, setInfoFormat] = useState(null)
    const [redistributeOpen, setRedistributeOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    // Un refus du serveur doit se LIRE : sans lui, le CTA se grisait et
    // l'écran ne disait rien.
    const [serverError, setServerError] = useState(null)
    // Section que le parent doit corriger après un clic sur « Continuer », et
    // le compteur de tentatives : le rappel tremble à chacune.
    const [sectionToFix, setSectionToFix] = useState(null)
    const [toFixNudge, setToFixNudge] = useState(0)
    const scrollRef = useRef(null)
    const { sectionRef, onSectionLayout, scrollToSection } =
        useFunnelSectionScroll(scrollRef)
    // Numéro du dernier enregistrement parti : le curseur enchaîne plusieurs
    // clics en une seconde, et deux devis peuvent revenir dans le désordre —
    // seule la réponse du DERNIER payload a le droit d'écrire le devis.
    const persistSeq = useRef(0)
    // L'enregistrement en attente : chaque clic remplace le précédent, un seul
    // part une fois la rafale passée (cocher cinq créneaux = une requête).
    const persistTimer = useRef(null)

    const firstname = state.studentFirstname || "votre enfant"
    const {
        askedWithCommitment,
        choice,
        rules: formulaRules,
        commitment,
        trackCommitment,
    } = useFormulaChoice({
        formula: { offerType, weeklyHours, hoursAllocation },
        subjectIds,
        served: { noCommitment, priceGrid, weeklyHoursByOffer },
        saving,
        quote,
        source: "configuration",
    })
    const lockedHours = hoursLockedTo(choice, formulaRules)
    // Bornes servies par le backend. Aucun repli fabriqué ici : sans réponse
    // serveur on laisse le picker libre plutôt que d'inventer une règle.
    const floor = startDateFloor ? moment(startDateFloor).toDate() : new Date()
    const ceiling = startDateCeiling
        ? moment(startDateCeiling).toDate()
        : undefined

    // Volumes vendables pour l'OFFRE choisie : les colonnes tarifaires n'ont
    // pas forcément les mêmes paliers.
    const sellableHours = weeklyHoursByOffer?.[offerType] || []

    // En dessous de la 3ᵉ, l'élève peut suivre plus de matières que d'heures :
    // au-delà du volume, l'accompagnement devient du SUIVI DE DEVOIRS. Au lycée
    // une matière veut son heure.
    const allowMoreSubjectsThanHours = allowsMoreSubjectsThanHours(
        state.studentClass
    )
    const minHoursForSubjects = allowMoreSubjectsThanHours
        ? 0
        : subjectIds.length
    const isHomeworkSupport =
        allowMoreSubjectsThanHours &&
        weeklyHours > 0 &&
        subjectIds.length > weeklyHours
    const subjectsExceedHours =
        weeklyHours > 0 &&
        !subjectsFitHours(state.studentClass, subjectIds.length, weeklyHours)
    // Les créneaux sont des DISPONIBILITÉS : au moins autant que d'heures
    // achetées, davantage est mieux, jamais de plafond.
    const minSlots = Math.max(1, weeklyHours)
    const notEnoughSlots = weeklyHours > 0 && slots.length < weeklyHours

    useEffect(() => {
        amplitude.track("inscription_funnel_configuration_viewed", {
            funnel_state_id: funnelStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const subjectOptions = useMemo(
        () =>
            (subjectsCatalogue || []).map((subject) => ({
                id: String(subject.id),
                label: subject.name,
                iconUri: subjectIconUri(subject.icon_path),
                // Sans programme pour la classe, la matière se vend mais passe
                // derrière le pli « + N autres matières ».
                secondary: subject.programme_available === false,
            })),
        [subjectsCatalogue]
    )
    const subjectById = useMemo(
        () => new Map(subjectOptions.map((o) => [o.id, o])),
        [subjectOptions]
    )
    const hoursOptions = useMemo(
        () =>
            sellableHours.map((h) => ({
                value: h,
                recommended: h === recommendedWeeklyHours,
            })),
        [sellableHours, recommendedWeeklyHours]
    )

    // Le cran de chaque matière retenue, le défaut pour celles que le parent
    // n'a pas touchées — c'est cette table qui part au serveur.
    const goalsOf = (ids, goals) =>
        Object.fromEntries(
            ids.map((id) => [
                String(id),
                goals[String(id)] ?? SUBJECT_GOAL_DEFAULT,
            ])
        )

    // Répartition automatique : parts égales, le reste aux premières matières
    // choisies. Vide quand le volume ne couvre pas les matières (devoirs).
    const defaultAllocation = (ids, hours) =>
        allocationFor(null, hours, { ...formulaRules, subjectIds: ids })

    /**
     * Écrit la formule et récupère le devis. Appelé à CHAQUE modification : le
     * parent doit voir son prix bouger quand il coche, pas au moment de valider.
     */
    const persist = (next = {}) => {
        const stateId = funnelStateId
        const payload = {
            subjects: next.subjects ?? subjectIds,
            subjectGoals: next.subjectGoals ?? subjectGoals,
            offerType: next.offerType ?? offerType,
            weeklyHours: next.weeklyHours ?? weeklyHours,
            withCommitment: next.withCommitment ?? askedWithCommitment,
            slots: next.slots ?? slots,
            startDate: next.startDate ?? startDate,
            hoursAllocation: next.hoursAllocation ?? hoursAllocation,
        }
        // Sans identifiant de parcours, RIEN ne part au serveur — et l'écran
        // continuerait à répondre aux clics sans jamais chiffrer. On le dit.
        if (!stateId) {
            setServerError(INSCRIPTION_FUNNEL_LOST)
            setQuote(null)
            return
        }

        const seq = ++persistSeq.current
        const isLatest = () => seq === persistSeq.current
        setSaving(true)
        update({ quoteStale: true })
        clearTimeout(persistTimer.current)
        persistTimer.current = setTimeout(() => {
            inscriptionFunnelService
                .updateInscriptionAnswers(
                    { stateId },
                    {
                        subjects: payload.subjects,
                        subjectGoals: goalsOf(
                            payload.subjects,
                            payload.subjectGoals
                        ),
                        offerType: payload.offerType,
                        weeklyHours: payload.weeklyHours || undefined,
                        // Toujours dit, même engagé : un dossier resté sans
                        // engagement alors que le serveur ne le vend plus ne
                        // retrouverait jamais de devis.
                        withCommitment: payload.withCommitment,
                        slotsPossible: payload.slots,
                        startDate: payload.startDate || undefined,
                        hoursAllocation: payload.hoursAllocation,
                    }
                )
                .then((res) => {
                    if (!isLatest()) return
                    setServerError(null)
                    const nextQuote = res?.data?.data?.quote ?? null
                    setQuote(nextQuote)
                    update({ quote: nextQuote, quoteStale: false })
                    // L'engagement vendu est celui du devis. S'il n'a pas
                    // suivi la demande, l'interrupteur se recale sur lui
                    // plutôt que de rester sur une promesse que personne ne
                    // tient.
                    const sold = !quoteIsWithoutCommitment(nextQuote)
                    if (nextQuote && sold !== payload.withCommitment) {
                        update({ withCommitment: sold, committedFormula: null })
                    }
                    // La date vendue est celle du serveur : proposée d'office (le
                    // plancher) quand le parent n'en a pas choisi, remontée au
                    // plancher ou au plafond sinon. On se recale dessus, sans quoi
                    // le picker rouvrirait sur une date qui n'est pas celle vendue.
                    if (
                        nextQuote?.start_date &&
                        nextQuote.start_date !== payload.startDate
                    ) {
                        setStartDate(nextQuote.start_date)
                        update({ startDate: nextQuote.start_date })
                    }
                })
                // Un refus se LIT : le parent doit savoir quoi corriger.
                .catch((err) => {
                    if (!isLatest()) return
                    setServerError(
                        answersRefusalMessage(
                            err,
                            "Votre formule n'a pas pu être enregistrée. Réessayez dans un instant."
                        )
                    )
                    setQuote(null)
                })
                .finally(() => {
                    if (isLatest()) setSaving(false)
                })
        }, PERSIST_DEBOUNCE_MS)
    }

    // Recharge le devis à l'arrivée quand la formule est déjà composée (retour
    // arrière depuis les masteurs, reprise par lien magique), ou quand celui
    // en mémoire date d'avant les dernières réponses (page rechargée avant le
    // retour d'un enregistrement).
    useEffect(() => {
        if (weeklyHours > 0 && slots.length > 0 && (!quote || state.quoteStale))
            persist()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // La page du format et la réservation écrivent le parcours alors que cet
    // écran reste monté dessous. Il suit la formule dès qu'elles l'écrivent :
    // un retour ici avant la réponse du serveur renverrait sinon celle
    // d'avant. Ses propres gestes posent la même valeur des deux côtés. Un
    // parcours sans offre est sur celle par défaut : c'est là qu'il revient
    // quand un choix fait ailleurs est refusé.
    useEffect(() => {
        const followedOffer = state.offerType || DEFAULT_OFFER_TYPE
        if (followedOffer !== offerType) setOfferType(followedOffer)
        if ((state.weeklyHours || 0) !== weeklyHours) {
            setWeeklyHours(state.weeklyHours || 0)
        }
        if (
            state.hoursAllocation &&
            state.hoursAllocation !== hoursAllocation
        ) {
            setHoursAllocation(state.hoursAllocation)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.offerType, state.weeklyHours, state.hoursAllocation])

    // Le devis et la date qu'il vend, eux, n'arrivent qu'avec lui. Un devis
    // que cet écran a lui-même posé n'apprend rien : il s'arrête là.
    useEffect(() => {
        if (!state.quote || state.quote === quote) return
        setQuote(state.quote)
        setStartDate(state.startDate || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.quote])

    const applySubjects = (next) => {
        const nextAllocation = defaultAllocation(next, weeklyHours)
        setSubjectIds(next)
        setHoursAllocation(nextAllocation)
        update({ subjects: next, hoursAllocation: nextAllocation })
        persist({ subjects: next, hoursAllocation: nextAllocation })
    }

    // Une matière que la classe ne propose pas se décoche, comme un volume que
    // l'offre ne vend pas — sans pastille dans le rail, on ne pourrait plus la
    // retirer. Ici et pas ailleurs : la reprise monte cet écran sous l'étape
    // masteurs, donc il passe avant tout paiement.
    useEffect(() => {
        const kept = subjectsOfferedBy(subjectIds, subjectsCatalogue)
        if (kept.length !== subjectIds.length) applySubjects(kept)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectsCatalogue])

    const onToggleSubject = (id) => {
        const next = subjectIds.includes(id)
            ? subjectIds.filter((s) => s !== id)
            : [...subjectIds, id]
        applySubjects(next)
        amplitude.track("inscription_funnel_subjects_selected", {
            funnel_state_id: funnelStateId,
            subjects_count: next.length,
        })
    }

    const onSetGoal = (id, slug) => {
        const next = { ...subjectGoals, [String(id)]: slug }
        setSubjectGoals(next)
        update({ subjectGoals: next })
        persist({ subjectGoals: next })
        amplitude.track("inscription_funnel_goal_selected", {
            funnel_state_id: funnelStateId,
            subject_id: Number(id),
            goal: slug,
        })
    }

    // Format, volume et engagement bougent ensemble : couper l'engagement
    // change la formule, choisir Solo le remet. Un seul enregistrement, d'un
    // choix que la règle rend complet.
    const applyChoice = (next) => {
        setOfferType(next.offerType)
        setWeeklyHours(next.weeklyHours)
        setHoursAllocation(next.hoursAllocation)
        update(next)
        persist(next)
    }

    const onPickFormat = (key) => {
        if (!formatIntroduced.current) openFormatInfo(key, "first_pick")
        const next = pickFormat(
            choice,
            INSCRIPTION_FORMATS[key]?.offerType,
            formulaRules
        )
        if (next === choice) return
        applyChoice(next)
        amplitude.track("inscription_funnel_format_selected", {
            funnel_state_id: funnelStateId,
            offer_type: next.offerType,
        })
        if (next.withCommitment !== choice.withCommitment) {
            trackCommitment(next.withCommitment)
        }
    }

    const onToggleCommitment = () => {
        const next = toggleCommitment(choice, formulaRules)
        applyChoice(next)
        trackCommitment(next.withCommitment)
    }

    // Le premier format touché ouvre son volet de lui-même : ce qui sépare
    // les deux formats ne se devine pas, et le parent n'a pas à aller le
    // chercher derrière le « i ». Une seule fois par visite de l'écran.
    const formatIntroduced = useRef(false)
    const openFormatInfo = (key, trigger) => {
        formatIntroduced.current = true
        setInfoFormat(key)
        amplitude.track("inscription_funnel_format_info_opened", {
            funnel_state_id: funnelStateId,
            offer_type: INSCRIPTION_FORMATS[key]?.offerType ?? key,
            trigger,
        })
    }
    const onOpenFormatInfo = (key) => openFormatInfo(key, "info")

    const onSetHours = (value) => {
        // Sans engagement, un seul volume se vend : les autres sont grisés
        // par cette même règle.
        if (lockedHours !== null && value !== lockedHours) return
        const nextAllocation = defaultAllocation(subjectIds, value)
        setWeeklyHours(value)
        setHoursAllocation(nextAllocation)
        update({ weeklyHours: value, hoursAllocation: nextAllocation })
        persist({ weeklyHours: value, hoursAllocation: nextAllocation })
        amplitude.track("inscription_funnel_hours_selected", {
            funnel_state_id: funnelStateId,
            weekly_hours: value,
        })
    }

    const onChangeSlots = (nextSlots) => {
        setSlots(nextSlots)
        update({ slots: nextSlots })
        persist({ slots: nextSlots })
        amplitude.track("inscription_funnel_slots_selected", {
            funnel_state_id: funnelStateId,
            slots_count: nextSlots.length,
        })
    }

    const onSaveAllocation = (allocation) => {
        setRedistributeOpen(false)
        setHoursAllocation(allocation)
        update({ hoursAllocation: allocation })
        persist({ hoursAllocation: allocation })
    }

    // DatePickerModalM (mode non contrôlé) renvoie déjà "YYYY-MM-DD" ; une
    // réponse toute faite de « Début des cours » aussi.
    const onDatePicked = (iso) => {
        setDateOpen(false)
        if (!iso) return
        setStartDate(iso)
        update({ startDate: iso })
        persist({ startDate: iso })
        amplitude.track("inscription_funnel_start_date_selected", {
            funnel_state_id: funnelStateId,
            start_date: iso,
        })
    }

    // Ce qui manque, dans l'ordre des questions — une seule chose à la fois.
    // `null` : rien ne bloque.
    const blocker = useMemo(() => {
        if (subjectIds.length === 0 || subjectsExceedHours) return "subjects"
        if (weeklyHours === 0) return "hours"
        if (slots.length === 0 || notEnoughSlots) return "slots"
        if (!quote) return "quote"
        return null
    }, [
        subjectIds.length,
        subjectsExceedHours,
        weeklyHours,
        slots.length,
        notEnoughSlots,
        quote,
    ])

    // Le blocage levé, la mise en avant s'efface ; déplacé, elle suit.
    useEffect(() => {
        if (!blocker) setSectionToFix(null)
        else if (sectionToFix && sectionToFix !== blocker)
            setSectionToFix(blocker)
    }, [blocker, sectionToFix])

    const goNext = () => {
        // « Continuer » ne se grise pas : il EMMÈNE le parent à l'endroit à
        // corriger et le met en avant, à chaque tentative.
        if (blocker) {
            amplitude.track("inscription_funnel_configuration_blocked", {
                funnel_state_id: funnelStateId,
                blocker,
                weekly_hours: weeklyHours,
                subjects_count: subjectIds.length,
                slots_count: slots.length,
                has_quote: !!quote,
                server_error: !!serverError,
            })
            setSectionToFix(blocker)
            setToFixNudge((n) => n + 1)
            // La mesure de repli ajoute l'espaceur du bandeau, au-dessus de la
            // feuille sur petit écran.
            scrollToSection(blocker, bandSpacerHeight(dimensions?.width ?? 0))
            return
        }

        amplitude.track("inscription_funnel_configuration_completed", {
            funnel_state_id: funnelStateId,
            offer_type: offerType,
            weekly_hours: weeklyHours,
            subjects_count: subjectIds.length,
        })
        navigation.navigate(
            formatOnOwnStep ? "inscription/format" : "inscription/tutors",
            nextParams
        )
    }

    // Une matière à zéro heure se MONTRE : masquer le zéro laissait croire
    // qu'elle serait servie.
    const hoursBySubject = Object.fromEntries(
        subjectIds.map((id) => [id, hoursAllocation[id] ?? 0])
    )
    const canRedistribute =
        subjectIds.length > 1 && weeklyHours > subjectIds.length

    // Le devis existe dès qu'il y a un volume, même quand les créneaux ne le
    // couvrent pas encore : l'afficher là annoncerait un tarif ferme sous un
    // refus. On dit à la place ce qu'il reste à faire, dans l'ordre du CTA.
    // Quand le format a sa page, le tarif se lit là-bas : ici, ce qui manque
    // empêche seulement de continuer.
    const toSeeThePrice = formatOnOwnStep
        ? "pour continuer"
        : "pour voir le tarif"
    const quoteHint =
        blocker === "subjects"
            ? `Choisissez vos matières ${toSeeThePrice}.`
            : blocker === "hours"
              ? `Choisissez vos heures ${toSeeThePrice}.`
              : blocker === "slots"
                ? slots.length === 0
                    ? `Indiquez les disponibilités ${elide(firstname)} ${toSeeThePrice}.`
                    : `Ajoutez ${minSlots - slots.length} créneau${minSlots - slots.length > 1 ? "x" : ""} ${toSeeThePrice}.`
                : null

    const startDateAdjustedMessage = quote?.start_date_adjusted
        ? startDateCeiling &&
          startDate &&
          moment(startDate).isSameOrAfter(moment(startDateCeiling), "day")
            ? "La date choisie était trop lointaine : nous avons pris la dernière date possible."
            : "La date choisie était trop proche : nous avons pris la première date possible."
        : null

    // Le devis existe mais quelque chose bloque encore : l'écran ne chiffre
    // rien, ni dans le bouton ni sur les cartes de date. Et sans le format,
    // pas de montant du tout : il dépend de lui.
    const priced = !!quote && !quoteHint && !formatOnOwnStep

    // La date VENDUE est celle du devis ; le temps d'un enregistrement, celle
    // que le parent vient de toucher.
    const shownStartDate = saving
        ? (startDate ?? quote?.start_date)
        : (quote?.start_date ?? startDate)
    const startOptions = startOptionCards(quote, { withAmounts: priced })
    // La carte cochée est celle que dit le devis : il la reconnaît au premier
    // cours, et une date du calendrier peut mener au même cours qu'une carte.
    // Seule une date que le parent vient de toucher se coche d'après elle,
    // le temps que son devis revienne.
    const dateInFlight =
        saving && !!startDate && startDate !== quote?.start_date
    const selectedStartOption = dateInFlight
        ? (startOptions.find((option) => option.startDate === startDate)?.key ??
          null)
        : (quote?.selected_start_option ?? null)

    const sheetFormats = formatSessions(firstname)

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.composition}
            scrollRef={scrollRef}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.configuration}
                    total={INSCRIPTION_STEPS.total}
                    onBack={() => navigation.goBack()}
                />
            }
            footer={
                <FormulaFooter
                    quote={priced ? quote : null}
                    // Cliquable même quand il manque quelque chose : c'est
                    // `goNext` qui emmène. Seul un enregistrement en cours le
                    // neutralise, le temps que le devis revienne.
                    disabled={saving}
                    onPress={goNext}
                />
            }
            overlay={
                <>
                    <SlotsPickerModal
                        isVisible={slotsOpen}
                        onClose={() => setSlotsOpen(false)}
                        onValidate={(next) => {
                            setSlotsOpen(false)
                            onChangeSlots(next)
                        }}
                        initialSlots={slots}
                        includeSunday={false}
                        minSlots={minSlots}
                        weeklyHours={weeklyHours}
                        userTimezone={props.userTimezone}
                        serverTimezone={props.serverTimezone}
                    />
                    <SummerSubjectHoursModal
                        isVisible={redistributeOpen}
                        onClose={() => setRedistributeOpen(false)}
                        subjects={subjectIds.map((id) => ({
                            id,
                            name: subjectById.get(id)?.label ?? "Matière",
                        }))}
                        weeklyHours={weeklyHours}
                        periodLabel="/semaine"
                        initialAllocation={hoursBySubject}
                        onSave={onSaveAllocation}
                    />
                    <FormatInfoSheet
                        visible={!!infoFormat}
                        initialKey={infoFormat}
                        formats={sheetFormats}
                        copy={formatInfoCopy(firstname)}
                        onClose={() => setInfoFormat(null)}
                    />
                    <DatePickerModalM
                        visible={dateOpen}
                        onDismiss={() => setDateOpen(false)}
                        value={
                            shownStartDate || moment(floor).format("YYYY-MM-DD")
                        }
                        onConfirm={onDatePicked}
                        // Les deux bornes viennent du serveur ; le dimanche n'est
                        // pas un jour de démarrage (la grille ne le vend pas).
                        validRange={{ startDate: floor, endDate: ceiling }}
                        disableWeekDays={[0]}
                        saveLabel="Valider"
                        label="Date de démarrage"
                    />
                </>
            }
        >
            <Text style={styles.title}>
                {INSCRIPTION_QUESTIONS.composer(firstname)}
            </Text>
            <InscriptionComposer
                firstname={firstname}
                subjectOptions={subjectOptions}
                subjectById={subjectById}
                subjectIds={subjectIds}
                maxSubjects={maxSubjectsFromServer || null}
                subjectsExceedHours={subjectsExceedHours}
                subjectsLoading={!subjectsCatalogue}
                onToggleSubject={onToggleSubject}
                subjectGoals={goalsOf(subjectIds, subjectGoals)}
                onSetGoal={onSetGoal}
                showFormat={!formatOnOwnStep}
                formatTiles={formatTiles({ choice, rules: formulaRules })}
                onPickFormat={onPickFormat}
                onOpenFormatInfo={onOpenFormatInfo}
                // Sur sa propre page, le format emmène l'interrupteur avec
                // lui. Il ne reste ici que coupé : c'est lui qui verrouille les
                // heures, il faut pouvoir le remettre là où le verrou se voit.
                commitment={
                    formatOnOwnStep && askedWithCommitment ? null : commitment
                }
                onToggleCommitment={onToggleCommitment}
                hoursOptions={hoursOptions}
                weeklyHours={weeklyHours}
                minHoursForSubjects={minHoursForSubjects}
                hoursLockedTo={lockedHours}
                onSetHours={onSetHours}
                isHomeworkSupport={isHomeworkSupport}
                hoursBySubject={hoursBySubject}
                canRedistribute={canRedistribute}
                onOpenRedistribute={() => setRedistributeOpen(true)}
                slots={slots}
                onOpenSlots={() => setSlotsOpen(true)}
                minSlots={minSlots}
                startDate={shownStartDate}
                quote={quote}
                startDateAdjustedMessage={startDateAdjustedMessage}
                onOpenDate={() => setDateOpen(true)}
                startOptions={startOptions}
                selectedStartOption={selectedStartOption}
                onPickStartOption={(key) => {
                    if (key === selectedStartOption) return
                    onDatePicked(
                        startOptions.find((option) => option.key === key)
                            ?.startDate
                    )
                }}
                priced={priced}
                serverError={serverError}
                quoteHint={quoteHint}
                sectionToFix={sectionToFix}
                toFixNudge={toFixNudge}
                sectionRef={sectionRef}
                onSectionLayout={onSectionLayout}
            />
            <SummerHelpBanner
                step={INSCRIPTION_STEPS.configuration}
                trackingEvent="inscription_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 4,
    },
})

const mapStateToProps = (state) => ({
    startDateFloor: state.data?.inscriptionFunnel?.startDateFloor,
    startDateCeiling: state.data?.inscriptionFunnel?.startDateCeiling,
    weeklyHoursByOffer: state.data?.inscriptionFunnel?.weeklyHoursByOffer,
    maxSubjectsFromServer: state.data?.inscriptionFunnel?.maxSubjects,
    recommendedWeeklyHours:
        state.data?.inscriptionFunnel?.recommendedWeeklyHours,
    noCommitment: state.data?.inscriptionFunnel?.noCommitment,
    priceGrid: state.data?.inscriptionFunnel?.priceGrid,
    // Les cases de la grille NOMMENT des heures de Paris et c'est ce qui part
    // au serveur ; seule l'étiquette se traduit. Sans `at` : la grille sert un
    // démarrage imminent, donc l'écart se mesure au render.
    //
    // Cf. resolveFunnelUserTimezone (repli sur l'appareil à la lecture).
    userTimezone: resolveFunnelUserTimezone(
        state.data?.inscriptionFunnel?.userTimezone
    ),
    serverTimezone: state.data?.inscriptionFunnel?.serverTimezone,
})

export default connect(mapStateToProps)(InscriptionConfigurationScreen)
